/**
 * Persistent key/value storage of the client, chosen once at startup (src/main.ts): the native app's Capacitor
 * Preferences (Android SharedPreferences / iOS UserDefaults: the OS never evicts them like a WebView's localStorage)
 * when the shell has the plugin, else the browser's localStorage, else memory. No cookies anywhere.
 *
 * Everything that persists goes through it: the backend credentials of each server origin (src/client/net/credentials.ts),
 * the game's SharedObjects (the platform storage of src/flash, prefix 'fwp:'), the offline backend's data and the server
 * setting. Their callers read synchronously, so the native store is loaded into memory before the game starts
 * (`createAppStorage`) and every change is written through to the plugin in order (`flush()` waits for the writes).
 */
import type { WebStorageLike } from '../../server/core/storage-local';
import { type NativePlugin, nativePlugin } from './capacitor';

export interface AppStorage extends WebStorageLike {
  readonly kind: 'native' | 'localStorage' | 'memory';
  /** Resolves when every change so far is persisted (native writes are asynchronous). */
  flush(): Promise<void>;
}

/** How long a start waits for the native store to load before it falls back to localStorage (ms). */
export const NATIVE_OPEN_TIMEOUT_MS = 5_000;
/** How long a restart or the exit waits for the native writes before it goes on anyway (ms). */
export const FLUSH_TIMEOUT_MS = 2_000;

/** `promise`'s value, or `fallback` when it has not settled within `ms` (or failed). */
function within<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

/**
 * Waits for the storage's pending writes (before the page reloads or the app exits: a native write still queued
 * would be lost), at most `timeoutMs`: a plugin call that never answers must not keep the game from restarting or the
 * app from closing. True when everything was written.
 */
export function flushWithin(storage: AppStorage | null, timeoutMs = FLUSH_TIMEOUT_MS): Promise<boolean> {
  if (!storage) return Promise.resolve(true);
  return within(
    storage.flush().then(() => true),
    timeoutMs,
    false,
  );
}

class MemoryAppStorage implements AppStorage {
  readonly kind: AppStorage['kind'] = 'memory';
  protected readonly map = new Map<string, string>();
  get length(): number {
    return this.map.size;
  }
  key(index: number): string | null {
    return [...this.map.keys()][index] ?? null;
  }
  getItem(key: string): string | null {
    return this.map.has(key) ? (this.map.get(key) as string) : null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, String(value));
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
  flush(): Promise<void> {
    return Promise.resolve();
  }
}

/** Capacitor Preferences behind a synchronous cache (loaded by `open`); writes go through in order. */
export class NativeAppStorage extends MemoryAppStorage {
  override readonly kind = 'native' as const;
  private chain: Promise<void> = Promise.resolve();
  private failures = 0;

  private constructor(private readonly plugin: NativePlugin) {
    super();
  }

  /**
   * Loads every stored key; null when the plugin fails or does not answer within `timeoutMs` (the caller falls back to
   * localStorage instead of leaving the game on a black screen).
   */
  static async open(plugin: NativePlugin, timeoutMs = NATIVE_OPEN_TIMEOUT_MS): Promise<NativeAppStorage | null> {
    const load = async (): Promise<NativeAppStorage> => {
      const store = new NativeAppStorage(plugin);
      const res = await plugin.call('keys');
      const keys: unknown[] = Array.isArray(res?.keys) ? res.keys : [];
      await Promise.all(
        keys.map(async (k) => {
          const key = String(k);
          const v = await plugin.call('get', { key });
          if (v && typeof v.value === 'string') store.map.set(key, v.value);
        }),
      );
      return store;
    };
    return within<NativeAppStorage | null>(load(), timeoutMs, null);
  }

  private write(method: 'set' | 'remove', options: Record<string, unknown>): void {
    this.chain = this.chain
      .then(() => this.plugin.call(method, options))
      .then(
        () => undefined,
        () => {
          this.failures++;
        },
      );
  }

  override setItem(key: string, value: string): void {
    const v = String(value);
    if (this.map.get(key) === v) return;
    super.setItem(key, v);
    this.write('set', { key, value: v });
  }

  override removeItem(key: string): void {
    if (!this.map.has(key)) return;
    super.removeItem(key);
    this.write('remove', { key });
  }

  override flush(): Promise<void> {
    return this.chain;
  }

  /** Native writes that failed (diagnostics). */
  get failedWrites(): number {
    return this.failures;
  }
}

/** The browser's localStorage (every access guarded: a refusal keeps the value in memory for this session). */
class LocalAppStorage extends MemoryAppStorage {
  override readonly kind = 'localStorage' as const;
  constructor(private readonly ls: WebStorageLike) {
    super();
  }
  private lsLength(): number {
    try {
      return this.ls.length;
    } catch {
      return 0;
    }
  }
  override get length(): number {
    return this.lsLength() + super.length;
  }
  override key(index: number): string | null {
    const n = this.lsLength();
    try {
      return index < n ? this.ls.key(index) : super.key(index - n);
    } catch {
      return null;
    }
  }
  override getItem(key: string): string | null {
    try {
      const v = this.ls.getItem(key);
      if (v !== null) return v;
    } catch {
      /* refused: memory below */
    }
    return super.getItem(key);
  }
  override setItem(key: string, value: string): void {
    try {
      this.ls.setItem(key, String(value));
      super.removeItem(key);
    } catch {
      super.setItem(key, value);
    }
  }
  override removeItem(key: string): void {
    super.removeItem(key);
    try {
      this.ls.removeItem(key);
    } catch {
      /* refused */
    }
  }
}

function browserLocalStorage(win: any): WebStorageLike | null {
  try {
    const ls = win?.localStorage;
    return ls && typeof ls.getItem === 'function' ? (ls as WebStorageLike) : null;
  } catch {
    return null;
  }
}

/** The storage of this start: native Preferences in the app shell, else localStorage, else memory. */
export async function createAppStorage(win: any = globalThis, opts: { openTimeoutMs?: number } = {}): Promise<AppStorage> {
  const prefs = nativePlugin('Preferences', win);
  if (prefs) {
    const native = await NativeAppStorage.open(prefs, opts.openTimeoutMs);
    if (native) return native;
    console.warn('[fwp] the native store did not load: this start keeps its data in the WebView storage');
  }
  const ls = browserLocalStorage(win);
  return ls ? new LocalAppStorage(ls) : new MemoryAppStorage();
}
